import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { assignmentsApi, learningPathsApi, orgApi, resultsApi } from '../../api/client';

type Variant = 'analytics' | 'status' | 'quiz' | 'reports' | 'leaderboard' | 'certification';

const TITLES: Record<Variant, string> = {
  analytics: 'Training Analytics',
  status: 'Training Status Overview',
  quiz: 'Quiz Performance Analytics',
  reports: 'Training Reports',
  leaderboard: 'Training Leaderboard',
  certification: 'Certification Compliance',
};

const COLORS = ['#4caf50', '#ff9800', '#9e9e9e', '#f44336', '#1976d2', '#9c27b0'];

function ChartBox({ title, empty, children }: { title: string; empty?: boolean; children: React.ReactElement }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {empty ? (
          <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
            No data for this chart yet
          </Typography>
        ) : (
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              {children}
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

const InsightsPage: React.FC<{ variant: Variant; storeId?: string | null }> = ({ variant, storeId }) => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [paths, setPaths] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [e, a, r, p] = await Promise.all([
          orgApi.listEmployees(storeId ? { storeId } : undefined),
          assignmentsApi.list(),
          resultsApi.list(),
          learningPathsApi.listAssignments(),
        ]);
        if (cancelled) return;
        const ids = new Set((e as any[]).map((emp) => emp.id));
        const scopedEmployees = e as any[];
        const scopedAssignments = storeId ? (a as any[]).filter((item) => ids.has(item.employeeId)) : (a as any[]);
        const assignmentIds = new Set(scopedAssignments.map((item) => item.id));
        const scopedResults = storeId
          ? (r as any[]).filter((item) => assignmentIds.has(item.assignmentId) || ids.has(item.assignment?.employeeId))
          : (r as any[]);
        const scopedPaths = storeId ? (p as any[]).filter((item) => ids.has(item.employeeId)) : (p as any[]);
        setEmployees(scopedEmployees);
        setAssignments(scopedAssignments);
        setResults(scopedResults);
        setPaths(scopedPaths);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const model = useMemo(() => {
    const complete = assignments.filter((a) => a.isTrainingComplete || a.status === 'completed');
    const inProgress = assignments.filter((a) => !complete.includes(a));
    const scores = results.map((r) => Number(r.score || 0));
    const avg = scores.length ? Math.round(scores.reduce((s, n) => s + n, 0) / scores.length) : 0;
    const passed = results.filter((r) => r.passed).length;
    const passRate = results.length ? Math.round((passed / results.length) * 100) : 0;
    const completionRate = assignments.length ? Math.round((complete.length / assignments.length) * 100) : 0;

    const byEmployee = employees.map((emp) => {
      const mine = assignments.filter((a) => a.employeeId === emp.id);
      const done = mine.filter((a) => a.isTrainingComplete || a.status === 'completed').length;
      const empResults = results.filter((r) => r.assignment?.employeeId === emp.id || r.assignment?.employee?.id === emp.id);
      const empAvg = empResults.length
        ? Math.round(empResults.reduce((s, r) => s + Number(r.score || 0), 0) / empResults.length)
        : 0;
      const progress = mine.length ? Math.round((done / mine.length) * 100) : 0;
      return {
        id: emp.id,
        name: emp.name || 'Employee',
        department: emp.department || 'Unassigned',
        total: mine.length,
        completed: done,
        inProgress: mine.length - done,
        notStarted: mine.filter((a) => a.status === 'assigned' && !a.isTrainingComplete).length,
        progress,
        score: empAvg,
        passed: empResults.filter((r) => r.passed).length,
      };
    });

    const started = byEmployee.filter((e) => e.completed > 0 || (e.inProgress > 0 && e.progress > 0)).length;
    const completedAll = byEmployee.filter((e) => e.total > 0 && e.completed === e.total).length;
    const notStarted = byEmployee.filter((e) => e.total === 0 || e.completed === 0).length;
    const overdue = paths.filter((p) => p.dueDate && new Date(p.dueDate) < new Date() && p.status !== 'completed').length;

    const statusPie = [
      { name: 'Completed all', value: completedAll, color: '#4caf50' },
      { name: 'In progress', value: Math.max(byEmployee.filter((e) => e.total > 0 && e.completed < e.total && e.completed > 0).length, 0), color: '#ff9800' },
      { name: 'Not started', value: byEmployee.filter((e) => e.total > 0 && e.completed === 0).length, color: '#9e9e9e' },
      { name: 'Overdue', value: overdue, color: '#f44336' },
    ].filter((d) => d.value > 0);

    const completionPie = [
      { name: 'Completed', value: complete.length, color: '#4caf50' },
      { name: 'Assigned', value: inProgress.length, color: '#1976d2' },
    ].filter((d) => d.value > 0);

    const progressRanges = [
      { range: '0%', count: byEmployee.filter((e) => e.total > 0 && e.progress === 0).length },
      { range: '1-25%', count: byEmployee.filter((e) => e.progress > 0 && e.progress <= 25).length },
      { range: '26-50%', count: byEmployee.filter((e) => e.progress > 25 && e.progress <= 50).length },
      { range: '51-75%', count: byEmployee.filter((e) => e.progress > 50 && e.progress <= 75).length },
      { range: '76-99%', count: byEmployee.filter((e) => e.progress > 75 && e.progress < 100).length },
      { range: '100%', count: byEmployee.filter((e) => e.total > 0 && e.progress === 100).length },
    ];

    const deptMap = new Map<string, { department: string; completed: number; inProgress: number; notStarted: number; overdue: number; progressSum: number; people: number }>();
    byEmployee.forEach((e) => {
      const row = deptMap.get(e.department) || { department: e.department, completed: 0, inProgress: 0, notStarted: 0, overdue: 0, progressSum: 0, people: 0 };
      row.completed += e.completed;
      row.inProgress += Math.max(e.total - e.completed, 0);
      if (e.total > 0 && e.completed === 0) row.notStarted += 1;
      row.progressSum += e.progress;
      row.people += 1;
      deptMap.set(e.department, row);
    });
    const departmentData = Array.from(deptMap.values()).map((d) => ({
      ...d,
      avgProgress: d.people ? Math.round(d.progressSum / d.people) : 0,
    }));

    const courseMap = new Map<string, { title: string; total: number; completed: number; scores: number[]; passed: number; attempts: number }>();
    assignments.forEach((a) => {
      const title = a.course?.title || 'Course';
      const row = courseMap.get(a.courseId) || { title, total: 0, completed: 0, scores: [] as number[], passed: 0, attempts: 0 };
      row.total += 1;
      if (a.isTrainingComplete || a.status === 'completed') row.completed += 1;
      courseMap.set(a.courseId, row);
    });
    results.forEach((r) => {
      const courseId = r.assignment?.courseId;
      const title = r.assignment?.course?.title || 'Course';
      if (!courseId) return;
      const row = courseMap.get(courseId) || { title, total: 0, completed: 0, scores: [] as number[], passed: 0, attempts: 0 };
      row.scores.push(Number(r.score || 0));
      row.attempts += 1;
      if (r.passed) row.passed += 1;
      courseMap.set(courseId, row);
    });
    const courseStats = Array.from(courseMap.values()).map((c) => ({
      title: c.title,
      total: c.total,
      completed: c.completed,
      completionRate: c.total ? Math.round((c.completed / c.total) * 100) : 0,
      averageScore: c.scores.length ? Math.round(c.scores.reduce((s, n) => s + n, 0) / c.scores.length) : 0,
      passRate: c.attempts ? Math.round((c.passed / c.attempts) * 100) : 0,
    }));

    const scoreBuckets = [
      { range: '0-59', count: scores.filter((n) => n < 60).length },
      { range: '60-69', count: scores.filter((n) => n >= 60 && n < 70).length },
      { range: '70-79', count: scores.filter((n) => n >= 70 && n < 80).length },
      { range: '80-89', count: scores.filter((n) => n >= 80 && n < 90).length },
      { range: '90-100', count: scores.filter((n) => n >= 90).length },
    ];

    const passPie = [
      { name: 'Passed', value: passed, color: '#4caf50' },
      { name: 'Failed', value: results.length - passed, color: '#f44336' },
    ].filter((d) => d.value > 0);

    const leaderboard = [...byEmployee].filter((e) => e.total > 0 || e.score > 0).sort((a, b) => b.score - a.score || b.completed - a.completed);

    const certPie = [
      { name: 'Valid', value: paths.filter((p) => p.certificationStatus === 'valid' || p.certificationStatus === 'active').length, color: '#4caf50' },
      { name: 'Expiring', value: paths.filter((p) => p.certificationStatus === 'expiring').length, color: '#ff9800' },
      { name: 'Expired', value: paths.filter((p) => p.certificationStatus === 'expired').length, color: '#f44336' },
      { name: 'In progress', value: paths.filter((p) => !p.certificationStatus || p.certificationStatus === 'pending').length, color: '#1976d2' },
    ].filter((d) => d.value > 0);

    return {
      complete: complete.length,
      avg,
      passed,
      passRate,
      completionRate,
      started,
      completedAll,
      notStarted,
      overdue,
      statusPie,
      completionPie,
      progressRanges,
      departmentData,
      courseStats,
      scoreBuckets,
      passPie,
      leaderboard,
      certPie,
    };
  }, [assignments, employees, paths, results]);

  const statCard = (label: string, value: string | number, color?: string) => (
    <Grid item xs={12} sm={6} md={3}>
      <Card>
        <CardContent>
          <Typography color="text.secondary" variant="body2">{label}</Typography>
          <Typography variant="h4" sx={{ color }}>{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{TITLES[variant]}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCard('Employees', employees.length)}
        {statCard('Assignments', assignments.length)}
        {statCard('Completed', model.complete, '#2e7d32')}
        {statCard(variant === 'quiz' ? 'Average score' : 'Completion', variant === 'quiz' ? `${model.avg}%` : `${model.completionRate}%`, '#1976d2')}
      </Grid>

      {(variant === 'status' || variant === 'analytics') && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <ChartBox title="Status Distribution" empty={model.statusPie.length === 0}>
              <PieChart>
                <Pie data={model.statusPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {model.statusPie.map((entry, i) => <Cell key={entry.name} fill={entry.color || COLORS[i]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartBox title="Completion Overview" empty={model.completionPie.length === 0}>
              <PieChart>
                <Pie data={model.completionPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {model.completionPie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartBox title="Progress Distribution" empty={!model.progressRanges.some((r) => r.count > 0)}>
              <BarChart data={model.progressRanges}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#2196f3" name="Employees" />
              </BarChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={8}>
            <ChartBox title="Department Breakdown" empty={model.departmentData.length === 0}>
              <BarChart data={model.departmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#4caf50" name="Completed" />
                <Bar dataKey="inProgress" stackId="a" fill="#ff9800" name="In Progress" />
                <Bar dataKey="notStarted" stackId="a" fill="#9e9e9e" name="Not Started" />
              </BarChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartBox title="Avg Progress by Department" empty={model.departmentData.length === 0}>
              <BarChart data={model.departmentData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="department" type="category" width={110} />
                <Tooltip />
                <Bar dataKey="avgProgress" fill="#2196f3" name="Average progress %" />
              </BarChart>
            </ChartBox>
          </Grid>
        </Grid>
      )}

      {variant === 'analytics' && (
        <Box sx={{ display: 'grid', gap: '1rem', mb: 2 }}>
          <Typography variant="h6">Course Performance</Typography>
          {model.courseStats.length === 0 && <Typography color="text.secondary">No course assignments yet.</Typography>}
          {model.courseStats.map((stat) => (
            <Box key={stat.title} sx={{ bgcolor: 'white', p: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{stat.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.completed} / {stat.total} completed</Typography>
                </Box>
                <Typography variant="h5" sx={{ color: '#1976d2' }}>{stat.completionRate}%</Typography>
              </Box>
              <Box sx={{ width: '100%', height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                <Box sx={{ width: `${stat.completionRate}%`, height: '100%', bgcolor: '#1976d2' }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Avg Score: <strong>{stat.averageScore}%</strong> · Pass Rate: <strong>{stat.passRate}%</strong>
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {variant === 'quiz' && (
        <Grid container spacing={2}>
          {statCard('Pass rate', `${model.passRate}%`, '#ed6c02')}
          {statCard('Quiz attempts', results.length)}
          <Grid item xs={12} md={4}>
            <ChartBox title="Pass / Fail" empty={model.passPie.length === 0}>
              <PieChart>
                <Pie data={model.passPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}>
                  {model.passPie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={8}>
            <ChartBox title="Score Distribution" empty={!model.scoreBuckets.some((b) => b.count > 0)}>
              <BarChart data={model.scoreBuckets}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" name="Attempts" />
              </BarChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12}>
            <ChartBox title="Average Score by Course" empty={model.courseStats.length === 0}>
              <BarChart data={model.courseStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="title" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="averageScore" fill="#4caf50" name="Average score" />
                <Bar dataKey="passRate" fill="#ff9800" name="Pass rate" />
              </BarChart>
            </ChartBox>
          </Grid>
        </Grid>
      )}

      {variant === 'reports' && (
        <ChartBox title="Completion by Course" empty={model.courseStats.length === 0}>
          <BarChart data={model.courseStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="title" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="completed" fill="#4caf50" name="Completed" />
            <Bar dataKey="total" fill="#1976d2" name="Assigned" />
          </BarChart>
        </ChartBox>
      )}

      {variant === 'leaderboard' && (
        <ChartBox title="Scores by Employee" empty={model.leaderboard.length === 0}>
          <BarChart data={model.leaderboard.slice(0, 12)} layout="vertical" margin={{ left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} />
            <YAxis dataKey="name" type="category" width={120} />
            <Tooltip />
            <Bar dataKey="score" fill="#1976d2" name="Average score" />
          </BarChart>
        </ChartBox>
      )}

      {variant === 'certification' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <ChartBox title="Certification Status" empty={model.certPie.length === 0}>
              <PieChart>
                <Pie data={model.certPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}>
                  {model.certPie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'grid', gap: '1rem' }}>
              {paths.length === 0 && <Typography color="text.secondary">No certification assignments yet.</Typography>}
              {paths.map((a) => (
                <Box key={a.id} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: '1.5rem', bgcolor: 'white' }}>
                  <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{a.employee?.name} → {a.learningPath?.title}</Typography>
                  <Typography color="text.secondary">Status: {a.status || '—'} · Certification: {a.certificationStatus || '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default InsightsPage;
